import { useEffect, useMemo, useState } from 'react'

import { Address } from 'viem'

import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiGetUserBalanceForToken } from '@src/api/machprotocol.api'
import {
    CHAIN_IDS,
    ChainId,
    ChainName,
    NATIVE_TOKEN_ADDRESS,
    WRAPPED_GAS_TOKENS,
    getChainNameFromChainId,
} from '@src/configs/chainInfo'
import { ResultCode } from '@src/enums'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { IBalanceResponse } from '@src/types/balance'
import { Balance, createContextLogger, isEVMAddress, isSolanaAddress, isTronAddress } from '@src/utils'
import { useApplicationStore, useHistoryStore, useTokensStore } from '@src/zustand_stores'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

const logger = createContextLogger('useExchangeBalances')

type BalanceQueryKey = [
    'balanceForToken' | 'balanceForTokens',
    string | undefined, // mainWalletAddress can be undefined
    number, // chainId
    string | string[], // token address(es)
]

const useExchangeBalances = () => {
    const queryClient = useQueryClient()

    const [nativeBalance, setNativeBalance] = useState<Balance>()
    const [wrappedNativeBalance, setWrappedNativeBalance] = useState<Balance>()

    const [isSellBalanceLoading, setIsSellBalanceLoading] = useState<boolean>(true)
    const [isBuyBalanceLoading, setIsBuyBalanceLoading] = useState<boolean>(true)
    const [isNativeBalanceLoading, setIsNativeBalanceLoading] = useState<boolean>(true)
    const [isWrappedNativeBalanceLoading, setIsWrappedNativeBalanceLoading] = useState<boolean>(true)

    const { tokens, isLoading: isTokensLoading, updateTokenBalancesWithBalances } = useTokensStore()
    const { isLoading: isAllBalancesLoading } = useFetchAllBalances()

    const { sellData, buyData, mainWalletAddress, destinationAddress, updateSellData, updateBuyData } =
        useExchangeDataContext()

    const [sellBalance, setSellBalance] = useState<Balance>()
    const [buyBalance, setBuyBalance] = useState<Balance>()

    const sellBalanceStabilized = useMemo(() => {
        return (
            sellBalance?.address.toLowerCase() === sellData.token.address.toLowerCase() &&
            sellBalance.chainId === sellData.token.chainId
        )
    }, [sellBalance, sellData.token])

    const buyBalanceStabilized = useMemo(() => {
        return (
            buyBalance?.address.toLowerCase() === buyData.token.address.toLowerCase() &&
            buyBalance.chainId === buyData.token.chainId
        )
    }, [buyBalance, buyData.token])

    const buySideAddress = useMemo<Address | undefined>(() => {
        if (destinationAddress) {
            return destinationAddress as Address
        } else {
            if (buyData.chainId === ChainId.Solana) {
                return isSolanaAddress(mainWalletAddress) ? (mainWalletAddress as Address) : undefined
            } else if (buyData.chainId === ChainId.Tron) {
                return isTronAddress(mainWalletAddress) ? (mainWalletAddress as Address) : undefined
            } else {
                return isEVMAddress(mainWalletAddress) ? (mainWalletAddress as Address) : undefined
            }
        }
    }, [sellData.chainId, buyData.chainId, mainWalletAddress, destinationAddress])

    const {
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        evmWalletAddress,
    } = useWalletConnections()

    const wrongWalletConnected = useMemo<boolean>(() => {
        if (sellData.chainId === ChainId.Solana) {
            return isSolanaConnected !== true
        } else if (sellData.chainId === ChainId.Tron) {
            return isTronConnected !== true
        } else {
            return isEVMConnected !== true
        }
    }, [
        sellData.chainId,
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        evmWalletAddress,
    ])

    const wrongBuySideAddress = useMemo<boolean>(() => {
        if (buyData.chainId === ChainId.Solana) {
            return !isSolanaAddress(buySideAddress)
        } else if (buyData.chainId === ChainId.Tron) {
            return !isTronAddress(buySideAddress)
        } else {
            return !isEVMAddress(buySideAddress)
        }
    }, [buyData.chainId, buySideAddress])

    // Update native balance and wrapped native balance when tokens object changes.
    useEffect(() => {
        const wrappedToken = tokens.find(
            t => t.chainId === sellData.chainId && t.address === WRAPPED_GAS_TOKENS[sellData.chainId]?.address
        )
        const _wrappedBalance = wrappedToken
            ? new Balance(wrappedToken).setValue(wrappedToken.userBalance ?? BigInt(0))
            : undefined
        setWrappedNativeBalance(_wrappedBalance)

        const nativeToken = tokens.find(
            t => t.chainId === sellData.chainId && t.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
        )
        const _nativeBalance = nativeToken
            ? new Balance(nativeToken).setValue(nativeToken.userBalance ?? BigInt(0))
            : undefined
        setNativeBalance(_nativeBalance)
    }, [tokens])

    // While tokens and balances are loading, set all internal loading states to true
    useEffect(() => {
        setIsSellBalanceLoading(isTokensLoading || isAllBalancesLoading)
        setIsBuyBalanceLoading(isTokensLoading || isAllBalancesLoading)
        setIsNativeBalanceLoading(isTokensLoading || isAllBalancesLoading)
        setIsWrappedNativeBalanceLoading(isTokensLoading || isAllBalancesLoading)
    }, [isTokensLoading, isAllBalancesLoading])

    /** --------------------------------------------------------------------------------------
     * TanStack Query for native token balance and wrapped native token balance
     * ---------------------------------------------------------------------------------------*/
    const {
        data: nativeAndWrappedNativeBalanceResponse,
        isFetching: isNativeAndWrappedNativeBalanceFetching,
        refetch: refetchWrappedNativeBalance,
    }: UseQueryResult<IBalanceResponse[], Error> = useQuery<
        IBalanceResponse[],
        Error,
        IBalanceResponse[],
        BalanceQueryKey
    >({
        queryKey: [
            'balanceForTokens',
            mainWalletAddress,
            sellData.chainId,
            [NATIVE_TOKEN_ADDRESS[sellData.chainId], WRAPPED_GAS_TOKENS[sellData.chainId].address || ''],
        ],
        queryFn: () =>
            (async (): Promise<IBalanceResponse[]> => {
                const results = await Promise.allSettled([
                    apiGetUserBalanceForToken(
                        mainWalletAddress!,
                        getChainNameFromChainId(sellData.chainId),
                        NATIVE_TOKEN_ADDRESS[sellData.chainId]
                    ),
                    apiGetUserBalanceForToken(
                        mainWalletAddress!,
                        getChainNameFromChainId(sellData.chainId),
                        WRAPPED_GAS_TOKENS[sellData.chainId].address
                    ),
                ])

                const aggregatedBalances = results.reduce<IBalanceResponse[]>((acc, result) => {
                    if (result.status === 'fulfilled') {
                        acc.push(result.value)
                    } else {
                        logger.error('Error fetching native/wrapped native balance:', result.reason)
                    }
                    return acc
                }, [])

                return aggregatedBalances
            })(),
        enabled: true,
        staleTime: 30000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: 'always',
        refetchOnMount: false,
    })

    const updateNativeAndWrappedNativeBalance = async () => {
        logger.info('[updateNativeAndWrappedNativeBalance]', `mainWalletAddress=${mainWalletAddress}`)
        if (mainWalletAddress) {
            await queryClient.invalidateQueries({
                queryKey: [
                    'balanceForTokens',
                    mainWalletAddress,
                    sellData.chainId,
                    [NATIVE_TOKEN_ADDRESS[sellData.chainId], WRAPPED_GAS_TOKENS[sellData.chainId].address || ''],
                ],
            })

            logger.info('[updateNativeAndWrappedNativeBalance] Cache invalidated, now refetching')

            await refetchWrappedNativeBalance()
        } else {
            const emsg = `Cannot update native and wrapped native balances: mainWalletAddress is not defined.`
            logger.error(emsg)
            setIsNativeBalanceLoading(false)
            setIsWrappedNativeBalanceLoading(false)
        }
    }

    useEffect(() => {
        updateNativeAndWrappedNativeBalance()
    }, [sellData.chainId])

    useEffect(() => {
        setIsNativeBalanceLoading(isNativeAndWrappedNativeBalanceFetching)
        setIsWrappedNativeBalanceLoading(isNativeAndWrappedNativeBalanceFetching)
    }, [isNativeAndWrappedNativeBalanceFetching])

    useEffect(() => {
        if (!isNativeAndWrappedNativeBalanceFetching && nativeAndWrappedNativeBalanceResponse) {
            updateTokenBalancesWithBalances(nativeAndWrappedNativeBalanceResponse)

            const native_resp = nativeAndWrappedNativeBalanceResponse.find(
                t => t.address === NATIVE_TOKEN_ADDRESS[CHAIN_IDS[t.chain as ChainName]]
            )
            const wnative_resp = nativeAndWrappedNativeBalanceResponse.find(
                t => t.address === WRAPPED_GAS_TOKENS[sellData.chainId].address
            )

            const native_token = tokens.find(
                t => t.chainId === sellData.chainId && t.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
            )
            const wnative_token = tokens.find(
                t => t.chainId === sellData.chainId && t.address === WRAPPED_GAS_TOKENS[sellData.chainId].address
            )

            if (!native_resp) {
                logger.error(`Cannot set native balance: native_resp not found`)
            } else if (!native_token) {
                logger.error(`Cannot set native balance: native_token not found`)
            } else {
                const newNativeBalance = new Balance(native_token).setValue(BigInt(native_resp.balance))
                logger.info(`[useEffect] Setting nativeBalance to`, newNativeBalance)
                setNativeBalance(newNativeBalance)
            }

            if (!wnative_resp) {
                logger.error(`Cannot set native balance: wnative_resp not found`)
            } else if (!wnative_token) {
                logger.error(`Cannot set native balance: wnative_token not found`)
            } else {
                const newWrappedNativeBalance = new Balance(wnative_token).setValue(BigInt(wnative_resp.balance))
                logger.info(`[useEffect] Setting wrappedNativeBalance to`, newWrappedNativeBalance)
                setWrappedNativeBalance(newWrappedNativeBalance)
            }

            setIsNativeBalanceLoading(false)
            setIsWrappedNativeBalanceLoading(false)
        }
    }, [isNativeAndWrappedNativeBalanceFetching, nativeAndWrappedNativeBalanceResponse])

    /** -------------------------------------------
     * TanStack Query for Sell Balance
     * --------------------------------------------*/
    const {
        data: sellBalanceResponse,
        dataUpdatedAt: sellBalanceUpdatedAt,
        error: sellBalanceError,
        isFetching: isSellBalanceFetching,
        refetch: refetchSellBalance,
    }: UseQueryResult<IBalanceResponse, Error> = useQuery<IBalanceResponse, Error, IBalanceResponse, BalanceQueryKey>({
        queryKey: ['balanceForToken', mainWalletAddress, sellData.chainId, sellData.token.address],
        queryFn: () =>
            apiGetUserBalanceForToken(
                mainWalletAddress!,
                getChainNameFromChainId(sellData.chainId),
                sellData.token.address
            ),
        enabled: !!mainWalletAddress && !!sellData.token.address && !!sellData.chainId,
        staleTime: 30000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: 'always',
        refetchOnMount: false,
    })

    useEffect(() => {
        logger.error(sellBalanceError)
    }, [sellBalanceError])

    // Modified update function for Sell Balance with force refresh option
    const updateSellBalance = async (forceRefresh = false) => {
        logger.info(
            '[updateSellBalance]',
            `mainWalletAddress=${mainWalletAddress}`,
            `sellData=`,
            sellData,
            `forceRefresh=${forceRefresh}`
        )
        if (mainWalletAddress) {
            const now = Date.now()
            if (!forceRefresh && sellBalanceResponse && now - sellBalanceUpdatedAt < 30000) {
                // Data is fresh, update state without refetching.
                const newSellBalance = new Balance(sellData.token).setValue(BigInt(sellBalanceResponse.balance))
                logger.info('Sell balance is fresh, updating local state without refetch.')
                setSellBalance(newSellBalance)
                setIsSellBalanceLoading(false)
            } else {
                // Force refresh or data is stale, refetch
                await refetchSellBalance()
            }
        } else {
            const emsg = `Cannot update sellBalance: mainWalletAddress is not defined.`
            logger.error(emsg)
            if (wrongWalletConnected) {
                setSellBalance(undefined)
            }
            setIsSellBalanceLoading(false)
        }
    }

    useEffect(() => {
        if (!isSellBalanceLoading && sellBalanceResponse) {
            updateSellData({
                token: { ...sellData.token, userBalance: BigInt(sellBalanceResponse.balance) },
            })
            updateTokenBalancesWithBalances([sellBalanceResponse])
            const newSellBalance = new Balance(sellData.token).setValue(BigInt(sellBalanceResponse.balance))
            logger.info(`[useEffect] Setting sellBalance to`, newSellBalance)
            setSellBalance(newSellBalance)
            setIsSellBalanceLoading(false)
        }
    }, [isSellBalanceLoading, sellBalanceResponse])

    useEffect(() => {
        setIsSellBalanceLoading(isSellBalanceFetching)
    }, [isSellBalanceFetching])

    /** -------------------------------------------
     * TanStack Query for Buy Balance
     * --------------------------------------------*/
    const {
        data: buyBalanceResponse,
        dataUpdatedAt: buyBalanceUpdatedAt,
        error: buyBalanceError,
        isFetching: isBuyBalanceFetching,
        refetch: refetchBuyBalance,
    }: UseQueryResult<IBalanceResponse, Error> = useQuery<IBalanceResponse, Error, IBalanceResponse, BalanceQueryKey>({
        queryKey: ['balanceForToken', buySideAddress, buyData.chainId, buyData.token.address],
        queryFn: () =>
            apiGetUserBalanceForToken(buySideAddress!, getChainNameFromChainId(buyData.chainId), buyData.token.address),
        enabled: !!buySideAddress && !!buyData.token.address && !!buyData.chainId,
        staleTime: 30000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: 'always',
        refetchOnMount: false,
    })

    useEffect(() => {
        logger.error(buyBalanceError)
    }, [buyBalanceError])

    // Modified update function for Buy Balance with force refresh option
    const updateBuyBalance = async (forceRefresh = false) => {
        logger.info(
            '[updateBuyBalance]',
            `buySideAddress=${buySideAddress}`,
            `buyData=`,
            buyData,
            `forceRefresh=${forceRefresh}`
        )
        if (buySideAddress) {
            if (wrongBuySideAddress) {
                setBuyBalance(undefined)
            } else {
                const now = Date.now()
                if (!forceRefresh && buyBalanceResponse && now - buyBalanceUpdatedAt < 30000) {
                    const newBuyBalance = new Balance(buyData.token).setValue(BigInt(buyBalanceResponse.balance))
                    logger.info('Buy balance is fresh, updating local state without refetch.')
                    setBuyBalance(newBuyBalance)
                    setIsBuyBalanceLoading(false)
                } else {
                    // Force refresh or data is stale, refetch
                    await refetchBuyBalance()
                }
            }
        } else {
            const emsg = `Cannot update buyBalance: buySideAddress is not defined.`
            logger.error(emsg)
            setBuyBalance(undefined)
            setIsBuyBalanceLoading(false)
        }
    }

    useEffect(() => {
        if (!isBuyBalanceLoading && buyBalanceResponse) {
            updateBuyData({
                token: { ...buyData.token, userBalance: BigInt(buyBalanceResponse.balance) },
            })
            updateTokenBalancesWithBalances([buyBalanceResponse])
            const newBuyBalance = new Balance(buyData.token).setValue(BigInt(buyBalanceResponse.balance))
            logger.info(`[useEffect] Setting buyBalance to`, newBuyBalance)
            setBuyBalance(newBuyBalance)
            setIsBuyBalanceLoading(false)
        }
    }, [isBuyBalanceLoading, buyBalanceResponse])

    useEffect(() => {
        setIsBuyBalanceLoading(isBuyBalanceFetching)
    }, [isBuyBalanceFetching])

    // Automatically update sellBalance when chain or token address changes
    useEffect(() => {
        if (
            sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId] &&
            buyData.token.address === NATIVE_TOKEN_ADDRESS[buyData.chainId]
        ) {
            return
        }
        updateSellBalance()
    }, [sellData.token.chainId, sellData.token.address, mainWalletAddress])

    // Automatically update buyBalance when chain or token address changes
    useEffect(() => {
        if (
            sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId] &&
            buyData.token.address === NATIVE_TOKEN_ADDRESS[buyData.chainId]
        ) {
            return
        }
        updateBuyBalance()
    }, [buyData.token.chainId, buyData.token.address, buySideAddress])

    // Listen for successful transactions to update balances
    const { exchangeId } = useApplicationStore()
    const getOrder = useHistoryStore(state => state.getOrder)
    const transactionData = getOrder(exchangeId)
    const invalidateBalances = useInvalidateBalances()

    // Add a new function that can be called externally
    const refreshAllBalancesAndWait = async (): Promise<void> => {
        // First invalidate all balance queries
        invalidateBalances()

        logger.info('[refreshAllBalancesAndWait] Starting refresh of all balances')

        // Then update the specific balances with forced refresh
        await Promise.all([
            updateNativeAndWrappedNativeBalance(), // Now properly invalidates and refreshes
            updateSellBalance(true),
            updateBuyBalance(true),
        ])

        logger.info('[refreshAllBalancesAndWait] All balances refreshed successfully')
        logger.debug('[refreshAllBalancesAndWait] Current transaction data:', transactionData)

        // Return when all balance updates are complete
        return Promise.resolve()
    }

    // Keep the existing effect for automatic refreshes
    useEffect(() => {
        if (transactionData?.result?.status === ResultCode.success) {
            refreshAllBalancesAndWait()
        }
    }, [transactionData?.result?.status])

    return {
        isSellBalanceLoading,
        isBuyBalanceLoading,
        sellBalanceStabilized,
        buyBalanceStabilized,
        nativeBalance,
        isNativeBalanceLoading,
        wrappedNativeBalance,
        isWrappedNativeBalanceLoading,
        sellBalance,
        buyBalance,
        // Add the new function to the return value
        refreshAllBalancesAndWait,
    }
}

export default useExchangeBalances
